/**
 * The manual print buttons for a check-in's devices — the same component on
 * both surfaces, so the two can't drift into offering different things:
 *
 *  - the session page's "Print" section renders every device (numbered, so the
 *    rows line up with the Devices section above it);
 *  - the device page's Done summary renders just that device.
 *
 * Which buttons appear, and which lane each addresses, is
 * `utils/checkInPrint.ts` — not this component. One press is one document: the
 * pair of labels a completed check-in produces is the AutoPrint binding's job,
 * and a reprint is almost never a reprint of both.
 *
 * Feedback is an inline Alert rather than a Snackbar, matching the rest of the
 * check-in pages — and unlike a toast it survives on a phone held at the
 * counter, where the sticky action bar covers the bottom of the screen.
 */

import React, { useEffect, useRef, useState } from 'react';
import { Alert, Stack, Tooltip } from '@mui/material';
import {
  IconArrowDown,
  IconArrowUp,
  IconChevronDown,
  IconChevronUp,
  IconFileText,
  IconPrinter,
} from '@tabler/icons-react';
import type {
  CheckInDraftJob,
  CheckInSession,
} from '@techgitter/contracts';

import { printRepairTicketLabel, printTicketSheet } from '../../../services/printApi';
import {
  checkInPrintButtons,
  type CheckInPrintAction,
  type CheckInPrintButton,
} from '../../../utils/checkInPrint';
import { checkInDeviceLabel } from '../deviceTypes';

export interface CheckInPrintActionsProps {
  session: CheckInSession;
  /** The devices to offer prints for — every draft job, or just one. */
  devices: CheckInDraftJob[];
  /**
   * Name each device above its buttons. The session page shows several at once
   * and needs them told apart; the device page is already about one machine.
   */
  showDeviceHeadings?: boolean;
  /**
   * Fired once a document has actually gone to a printer, so the host can mark
   * its Print section done.
   *
   * **A send, not a press.** A skipped print (no printer configured) or a
   * failed one leaves the paperwork unprinted, and a section marked done over
   * the top of that notice would be the one lie in the panel.
   */
  onPrinted?: () => void;
}


const ACTION_ICON: Record<CheckInPrintAction, React.ComponentType<{ size?: number; stroke?: number }>> = {
  'job-label-upstairs': IconArrowUp,
  'job-label-downstairs': IconArrowDown,
  'ticket-sheet': IconFileText,
};

/** Where a successful send went, for the confirmation line. */
const ACTION_DESTINATION: Record<CheckInPrintAction, string> = {
  'job-label-upstairs': 'the workshop printer',
  'job-label-downstairs': 'the counter printer',
  'ticket-sheet': 'the office printer',
};

/**
 * The one RepairShopr ticket these devices share, or null.
 *
 * Under `single_job` every device carries the same ticket, so a per-device
 * button row offered the same document N times over — pressing "Job label" on
 * device 3 printed device 1's label, because there is only one label. Null for
 * `job_per_device` (several tickets, so the per-device rows are the only honest
 * shape) and for a single device, where the consolidated form would buy
 * nothing and would change what one press means.
 */
function sharedTicketId(devices: CheckInDraftJob[]): string | null {
  if (devices.length < 2) return null;
  const ids = new Set(
    devices.filter(d => d.repairShoprTicketId).map(d => String(d.repairShoprTicketId)),
  );
  return ids.size === 1 ? [...ids][0] : null;
}

/** Copies for one document: a number field with up/down, floored at 1. */
const QtyStepper: React.FC<{
  value: number;
  onChange: (next: number) => void;
  disabled?: boolean;
  label: string;
}> = ({ value, onChange, disabled, label }) => (
  <span className="ci-print-qty">
    <input
      type="number"
      min={1}
      className="ci-print-qty__field"
      value={value}
      disabled={disabled}
      aria-label={`${label} copies`}
      onChange={e => {
        const next = Number.parseInt(e.target.value, 10);
        onChange(Number.isFinite(next) && next >= 1 ? next : 1);
      }}
    />
    <span className="ci-print-qty__steps">
      <button
        type="button"
        onClick={() => onChange(value + 1)}
        disabled={disabled}
        aria-label={`One more ${label.toLowerCase()}`}
      >
        <IconChevronUp size={11} stroke={2} />
      </button>
      <button
        type="button"
        onClick={() => onChange(Math.max(1, value - 1))}
        disabled={disabled || value <= 1}
        aria-label={`One fewer ${label.toLowerCase()}`}
      >
        <IconChevronDown size={11} stroke={2} />
      </button>
    </span>
  </span>
);

export const CheckInPrintActions: React.FC<CheckInPrintActionsProps> = ({
  session,
  devices,
  showDeviceHeadings = true,
  onPrinted,
}) => {
  // One key at a time — `${jobId}:${action}` — so a press disables its own
  // button rather than the whole section.
  const [printing, setPrinting] = useState<string | null>(null);
  const [notice, setNotice] = useState<{
    severity: 'success' | 'warning' | 'error';
    message: string;
  } | null>(null);

  const shared = sharedTicketId(devices);
  // One label per device plus a spare for the job's own paperwork, and one
  // sheet for the job — there being one job. Held per action so each row keeps
  // its own count.
  const labelDefault = devices.length + 1;
  const [copies, setCopies] = useState<Record<string, number>>({});
  const copiesTouched = useRef(false);
  const countFor = (action: CheckInPrintAction): number =>
    copies[action] ?? (action === 'ticket-sheet' ? 1 : labelDefault);

  // Follow the device count until the operator sets a number themselves —
  // adding a fourth device should move the default off three, but must not
  // overwrite a figure somebody just typed.
  useEffect(() => {
    if (copiesTouched.current) return;
    setCopies({});
  }, [labelDefault]);

  const setCount = (action: CheckInPrintAction, next: number) => {
    copiesTouched.current = true;
    setCopies(prev => ({ ...prev, [action]: next }));
  };

  if (devices.length === 0) {
    return (
      <div className="ci-print-empty">No devices on this check-in to print for.</div>
    );
  }

  /** One document, `copies` times. Returns the result; never throws. */
  const send = async (
    ticketId: string,
    button: CheckInPrintButton,
    count: number,
  ): Promise<{ skipped?: boolean; reason?: string; jobId?: string } | Error> => {
    try {
      return button.action === 'ticket-sheet'
        ? await printTicketSheet(ticketId, { copies: count })
        : await printRepairTicketLabel(ticketId, { role: button.role, copies: count });
    } catch (err) {
      return err instanceof Error ? err : new Error('Print failed');
    }
  };

  const run = async (job: CheckInDraftJob, button: CheckInPrintButton) => {
    const ticketId = job.repairShoprTicketId;
    if (!ticketId) return;
    const key = `${job.id}:${button.action}`;
    setPrinting(key);
    setNotice(null);
    try {
      const result =
        button.action === 'ticket-sheet'
          ? await printTicketSheet(String(ticketId))
          : await printRepairTicketLabel(String(ticketId), { role: button.role });
      if (result.skipped) {
        setNotice({
          severity: 'warning',
          message: `Print skipped: ${result.reason ?? 'printer not configured'}`,
        });
      } else {
        setNotice({
          severity: 'success',
          message:
            `${button.label} sent to ${ACTION_DESTINATION[button.action]}`
            + `${result.jobId ? ` (job ${result.jobId.slice(0, 8)})` : ''}`,
        });
        onPrinted?.();
      }
    } catch (err) {
      setNotice({
        severity: 'error',
        message: err instanceof Error ? err.message : 'Print failed',
      });
    } finally {
      setPrinting(null);
    }
  };

  return (
    <Stack spacing={1.5}>
      {notice && (
        <Alert severity={notice.severity} onClose={() => setNotice(null)}>
          {notice.message}
        </Alert>
      )}

      {shared && (
        <SharedTicketPrints
          session={session}
          devices={devices}
          ticketId={shared}
          countFor={countFor}
          setCount={setCount}
          send={send}
          printing={printing}
          setPrinting={setPrinting}
          setNotice={setNotice}
          onPrinted={onPrinted}
        />
      )}

      {!shared && devices.map((job, idx) => {
        const buttons = checkInPrintButtons(session, job);
        return (
          <div className="ci-print-device" key={job.id}>
            {showDeviceHeadings && (
              <div className="ci-print-device__title">
                Device {idx + 1} · {checkInDeviceLabel(job)}
                {job.repairShoprTicketNumber && (
                  <span className="ci-print-device__ticket">
                    {' '}
                    · RS #{job.repairShoprTicketNumber}
                  </span>
                )}
              </div>
            )}
            <div className="ci-print-device__actions">
              {buttons.map(button => {
                const key = `${job.id}:${button.action}`;
                const Icon = ACTION_ICON[button.action];
                const pending = printing === key;
                const element = (
                  <button
                    type="button"
                    className="ci-btn ci-btn--ghost ci-btn--sm"
                    onClick={() => run(job, button)}
                    disabled={Boolean(button.unavailableReason) || printing !== null}
                    aria-label={button.title}
                  >
                    <Icon size={15} stroke={1.5} />
                    {pending ? 'Printing…' : button.label}
                  </button>
                );
                return (
                  <Tooltip
                    key={key}
                    title={button.unavailableReason ?? button.title}
                    arrow
                  >
                    {/* span wrapper so the Tooltip works on a disabled button */}
                    <span>{element}</span>
                  </Tooltip>
                );
              })}
            </div>
          </div>
        );
      })}
    </Stack>
  );
};

/**
 * The consolidated controls for devices that share one ticket.
 *
 * One row per document rather than per device — there is one label and one
 * sheet for the job, however many machines it covers — each with its own copy
 * count, plus a Print All that fires every row at its current count.
 */
const SharedTicketPrints: React.FC<{
  session: CheckInSession;
  devices: CheckInDraftJob[];
  ticketId: string;
  countFor: (action: CheckInPrintAction) => number;
  setCount: (action: CheckInPrintAction, next: number) => void;
  send: (
    ticketId: string, button: CheckInPrintButton, count: number,
  ) => Promise<{ skipped?: boolean; reason?: string; jobId?: string } | Error>;
  printing: string | null;
  setPrinting: (key: string | null) => void;
  setNotice: (n: { severity: 'success' | 'warning' | 'error'; message: string } | null) => void;
  onPrinted?: () => void;
}> = ({
  session, devices, ticketId, countFor, setCount, send, printing, setPrinting, setNotice,
  onPrinted,
}) => {
  // Lane rules stay in `checkInPrintButtons` — standard intake prints
  // downstairs only, express offers both floors. Taken from a ticketed device
  // so the no-ticket reason is not applied to a job that plainly has one.
  const sample = devices.find(d => d.repairShoprTicketId) ?? devices[0];
  const buttons = checkInPrintButtons(session, sample);
  const busy = printing !== null;

  const runOne = async (button: CheckInPrintButton) => {
    const count = countFor(button.action);
    setPrinting(`shared:${button.action}`);
    setNotice(null);
    const result = await send(ticketId, button, count);
    setPrinting(null);
    if (result instanceof Error) {
      setNotice({ severity: 'error', message: result.message });
      return;
    }
    if (result.skipped) {
      setNotice({
        severity: 'warning',
        message: `Print skipped: ${result.reason ?? 'printer not configured'}`,
      });
      return;
    }
    setNotice({
      severity: 'success',
      message:
        `${count} × ${button.label} sent to ${ACTION_DESTINATION[button.action]}`
        + `${result.jobId ? ` (job ${result.jobId.slice(0, 8)})` : ''}`,
    });
    onPrinted?.();
  };

  const runAll = async () => {
    setPrinting('shared:all');
    setNotice(null);
    // Sequential, not parallel: these go to different printers through one
    // agent, and a readable failure ("the sheet was skipped") is worth more
    // than the fraction of a second concurrency would save.
    const sent: string[] = [];
    const problems: string[] = [];
    for (const button of buttons) {
      const count = countFor(button.action);
      const result = await send(ticketId, button, count);
      if (result instanceof Error) {
        problems.push(`${button.label}: ${result.message}`);
      } else if (result.skipped) {
        problems.push(`${button.label}: ${result.reason ?? 'printer not configured'}`);
      } else {
        sent.push(`${count} × ${button.label}`);
      }
    }
    setPrinting(null);
    // Anything that actually went counts, even on a partial run — the operator
    // has paper in hand for it.
    if (sent.length > 0) onPrinted?.();
    if (problems.length === 0) {
      setNotice({ severity: 'success', message: `Sent ${sent.join(', ')}.` });
    } else {
      setNotice({
        severity: sent.length > 0 ? 'warning' : 'error',
        message:
          (sent.length > 0 ? `Sent ${sent.join(', ')}. ` : '')
          + `Not sent — ${problems.join('; ')}.`,
      });
    }
  };

  return (
    <div className="ci-print-device">
      <div className="ci-print-device__title">
        {devices.length} devices on one job
        {sample.repairShoprTicketNumber && (
          <span className="ci-print-device__ticket"> · RS #{sample.repairShoprTicketNumber}</span>
        )}
      </div>

      {buttons.map(button => {
        const Icon = ACTION_ICON[button.action];
        const pending = printing === `shared:${button.action}`;
        return (
          <div className="ci-print-doc" key={button.action}>
            <Tooltip title={button.unavailableReason ?? button.title} arrow>
              <span className="ci-print-doc__btn">
                <button
                  type="button"
                  className="ci-btn ci-btn--ghost ci-btn--sm"
                  onClick={() => runOne(button)}
                  disabled={Boolean(button.unavailableReason) || busy}
                  aria-label={button.title}
                >
                  <Icon size={15} stroke={1.5} />
                  {pending ? 'Printing…' : `Print ${button.label}`}
                </button>
              </span>
            </Tooltip>
            <QtyStepper
              value={countFor(button.action)}
              onChange={next => setCount(button.action, next)}
              disabled={Boolean(button.unavailableReason) || busy}
              label={button.label}
            />
          </div>
        );
      })}

      <button
        type="button"
        className="ci-btn ci-btn--ghost ci-btn--sm ci-print-all"
        onClick={runAll}
        disabled={buttons.some(b => b.unavailableReason) || busy}
      >
        <IconPrinter size={15} stroke={1.5} />
        {printing === 'shared:all' ? 'Printing…' : 'Print All'}
      </button>
    </div>
  );
};

/** The Done-summary variant: one device, no heading, behind a section band. */
export const CheckInDevicePrintActions: React.FC<{
  session: CheckInSession;
  draftJob: CheckInDraftJob;
}> = ({ session, draftJob }) => (
  <CheckInPrintActions session={session} devices={[draftJob]} showDeviceHeadings={false} />
);
